import {
  type CreatePaymentResponse,
  type CustomerOrder,
  createPaymentResponseSchema,
  customerOrderSchema,
  IDENTITY_ERROR_CODES,
  type OrderDraftRequest,
  orderResponseSchema,
  type Product,
  type ProductOptions,
  type QuoteResponse,
  quoteResponseSchema,
  type ScriptQuoteOptions,
} from '@noconflict/contracts';
import { ApiRequestError, apiFetch } from '../../shared/api/http';
import { identityErrorMessage } from '../../shared/api/identity-error';
import type { Supabase } from '../../shared/api/supabase';

/**
 * Orders as a customer sees them: reads come from the view `customer_orders` (never the table), which has no list
 * amount and no discount and nulls the amounts when the org's billing amounts are hidden. Writes go through the
 * worker, which is the only place an order is priced or changes state.
 */

/** One order, or null when the caller may not see it (no `projects.view` and no `orders.pay` on its workspace). */
export async function getOrder(client: Supabase, orderId: string): Promise<CustomerOrder | null> {
  const { data, error } = await client
    .from('customer_orders')
    .select('*')
    .eq('id', orderId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? customerOrderSchema.parse(data) : null;
}

/** The project's live main order (draft, awaiting payment or placed), or null. */
export async function getProjectOrder(
  client: Supabase,
  projectId: string,
): Promise<CustomerOrder | null> {
  const { data, error } = await client
    .from('customer_orders')
    .select('*')
    .eq('project_id', projectId)
    .eq('kind', 'main')
    .in('status', ['draft', 'awaiting_payment', 'placed'])
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? customerOrderSchema.parse(data) : null;
}

/**
 * The live main order of every project in a workspace the caller can see: what the workspace page needs to tell
 * a draft from an order awaiting payment.
 */
export async function listWorkspaceOrders(
  client: Supabase,
  workspaceId: string,
): Promise<CustomerOrder[]> {
  const { data, error } = await client
    .from('customer_orders')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('kind', 'main')
    .in('status', ['draft', 'awaiting_payment', 'placed']);
  if (error) throw new Error(error.message);
  return customerOrderSchema.array().parse(data);
}

/**
 * Every order of an organization the caller can see, from awaiting payment onward, newest first: the Orders page.
 * The view decides the rows (`projects.view` or `orders.pay` on each order's workspace); drafts stay in the
 * workspace under "Continue your order".
 */
export async function listOrgOrders(
  client: Supabase,
  organizationId: string,
): Promise<CustomerOrder[]> {
  const { data, error } = await client
    .from('customer_orders')
    .select('*')
    .eq('organization_id', organizationId)
    .neq('status', 'draft')
    .order('submitted_at', { ascending: false, nullsFirst: false })
    .order('id');
  if (error) throw new Error(error.message);
  return customerOrderSchema.array().parse(data);
}

const body = (value: unknown) => JSON.stringify(value);

export const saveOrderDraft = (token: string, projectId: string, draft: OrderDraftRequest) =>
  apiFetch(`/api/projects/${projectId}/orders`, orderResponseSchema, {
    method: 'POST',
    token,
    body: body(draft),
  }).then((r) => r.order);

export const updateOrderDraft = (token: string, orderId: string, draft: OrderDraftRequest) =>
  apiFetch(`/api/orders/${orderId}`, orderResponseSchema, {
    method: 'PATCH',
    token,
    body: body(draft),
  }).then((r) => r.order);

/** Submits a draft. The browser's time zone goes with it, so the confirmation can show the due date as the form did. */
export const submitOrder = (token: string, orderId: string) =>
  apiFetch(`/api/orders/${orderId}/submit`, orderResponseSchema, {
    method: 'POST',
    token,
    body: body({ timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
  }).then((r) => r.order);

/** "Edit order": returns an order awaiting payment to draft, so it can be changed and is priced again. */
export const reopenOrder = (token: string, orderId: string) =>
  apiFetch(`/api/orders/${orderId}/reopen`, orderResponseSchema, { method: 'POST', token }).then(
    (r) => r.order,
  );

export const createOrderPayment = (
  token: string,
  orderId: string,
): Promise<CreatePaymentResponse> =>
  apiFetch(`/api/orders/${orderId}/payment`, createPaymentResponseSchema, {
    method: 'POST',
    token,
  });

export const getQuote = <P extends Product>(
  token: string,
  organizationId: string,
  product: P,
  options: ProductOptions[P],
): Promise<QuoteResponse> =>
  apiFetch('/api/pricing/quote', quoteResponseSchema, {
    method: 'POST',
    token,
    body: body({ organizationId, product, options }),
  });

/** A script is quoted by project: the worker reads the length from the project's analysis, never from here. */
export const getScriptQuote = (
  token: string,
  organizationId: string,
  projectId: string,
  options: ScriptQuoteOptions,
): Promise<QuoteResponse> =>
  apiFetch('/api/pricing/quote', quoteResponseSchema, {
    method: 'POST',
    token,
    body: body({ organizationId, product: 'script', projectId, options }),
  });

const identityCodes = new Set<string>(IDENTITY_ERROR_CODES);

/** Catalogue text for a failed order call: the worker passes a database refusal through as its error code. */
export function orderErrorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (identityCodes.has(error.code)) return identityErrorMessage(new Error(error.code));
    return error.message;
  }
  // an RPC called directly raises its code as the message; anything else gets the generic line
  return identityErrorMessage(error);
}

/** Does this org require a reference on every order? Yes or no; the profile itself stays unread. */
export async function getOrderReferenceRequired(
  client: Supabase,
  organizationId: string,
): Promise<boolean> {
  const { data, error } = await client.rpc('order_reference_required', {
    p_organization_id: organizationId,
  });
  if (error) throw new Error(error.message);
  return data === true;
}

/**
 * May the caller start a run in this project? `ok`, `order_required` (no placed order yet) or
 * `forbidden`. The database decides, for customers and staff alike; the worker asks the same function before it
 * starts a run, so this only decides what the page shows.
 */
export async function getRunStartCheck(
  client: Supabase,
  projectId: string,
): Promise<'ok' | 'order_required' | 'forbidden'> {
  const { data, error } = await client.rpc('run_start_check', { p_project_id: projectId });
  if (error) throw new Error(error.message);
  return data === 'ok' || data === 'order_required' ? data : 'forbidden';
}
